import { checkPageTargeting } from '../user-guide/helpers/pageTargetingHelper';
import { evaluateSegmentationRules } from '../segmentation/helpers/segmentationHelper';

export interface CandidateBot {
  id: string;
  // The bot's version, per bot in the embed-config: 3 and above is the new chat (chatV3.ts),
  // anything else (or absent, from an older backend) the player.
  version?: number | null;
  embedFormat?: 'widget' | 'iframe' | 'launcher';
  // Como o agente aparece: 'manual' significa que ele só se mostra quando a plataforma do
  // cliente chama `Getdemo.open()`. Vem por bot no embed-config (de BotIframeConfig).
  triggerMode?: 'auto' | 'manual' | null;
  pageTargeting?: { condition: 'AND' | 'OR'; rules: Array<{ url: string; rule: string }> } | null;
  widgetPriority?: number | null;
  audienceSegmentation?: {
    audienceType: 'all' | 'custom';
    segmentsCondition?: 'AND' | 'OR';
    segments?: Array<{ id?: string; rules: any }>;
  } | null;
  apiChannel?: boolean;
  channels?: string[];
  allowedDomains?: string[] | null;
  apiChannelConfig?: { allowedDomains?: string[] | null } | null;
}

export interface VisitorContext {
  url: string;
  customParams: Record<string, any>;
  language: string;
  timezone: string;
  /**
   * O ambiente e a identidade do visitante, para as condições de segmento que NÃO são
   * `CUSTOM_*`: `DEVICE`, `BROWSER`, `OS` e `USER_ID`.
   *
   * Sem isto o avaliador lia `undefined` nesses campos e a condição reprovava — e como os
   * filtros de um segmento se combinam com E, UMA condição dessas bastava para o agente nunca
   * ficar elegível, mesmo para quem o painel dizia ter acesso.
   */
  device?: string;
  browser?: string;
  os?: string;
  userId?: string | null;
  debug?: boolean;
}

// "Quem": mirrors BotPlayer.isBotEligible — fail-open (eligible) on error.
const audienceMatches = (bot: CandidateBot, ctx: VisitorContext): boolean => {
  const audience = bot.audienceSegmentation;
  if (!audience || audience.audienceType !== 'custom') return true;
  const segments = audience.segments || [];
  if (!segments.length) return true;
  try {
    // Espelha o `guideUser` que `getUserFieldValue` espera. `FIRST_INTERACTION` fica de fora de
    // propósito: depende de histórico que só o backend tem, e inventar um valor aqui seria pior
    // do que a condição não casar.
    const user: any = {
      customParams: ctx.customParams || {},
      language: ctx.language,
      device: ctx.device,
      browser: ctx.browser,
      os: ctx.os,
      customId: ctx.userId ?? undefined
    };
    const results = segments.map(s => evaluateSegmentationRules(s.rules, user, ctx.timezone));
    return audience.segmentsCondition === 'AND' ? results.every(Boolean) : results.some(Boolean);
  } catch {
    return true;
  }
};

// Hash-routed SPAs (React HashRouter, hashbang "#!/", Angular hash strategy) put the
// logical route after the "#". Page rules are authored against the logical path
// ("/about", "https://app/about"), so we collapse the hash-route into a real path before
// matching: "https://app.com/x/#/about?tab=2" → "https://app.com/about?tab=2". This is a
// SINGLE canonical URL (not an OR of raw+normalized), so negative operators like
// NOT_CONTAINS stay correct. Non-route hashes (in-page anchors like "#section") and
// path-routed URLs are returned unchanged.
export const normalizeRoutingUrl = (href: string): string => {
  const hashIdx = href.indexOf('#');
  if (hashIdx === -1) return href;
  let frag = href.slice(hashIdx + 1);
  if (frag.startsWith('!')) frag = frag.slice(1); // hashbang: #!/about
  if (!frag.startsWith('/')) return href; // anchor, not a route
  try {
    // Hash routers serve every route from the origin, so the pre-hash path is irrelevant.
    return new URL(href.slice(0, hashIdx)).origin + frag;
  } catch {
    return href;
  }
};

// "Onde": reuses the Tour Guide page-targeting matcher against the normalized URL. A bot
// with no rules matches anywhere. A throwing rule (e.g. invalid regex) is treated as
// "no match" so one bad rule never kills resolution.
const pageMatches = (bot: CandidateBot, url: string): boolean => {
  if (!bot.pageTargeting || !bot.pageTargeting.rules?.length) return true;
  try {
    return checkPageTargeting(bot.pageTargeting as any, url);
  } catch {
    return false;
  }
};

// Picks the agent for the current visitor: highest widgetPriority among bots whose
// audience AND page rules match, ties broken deterministically by id. When nothing
// matches it falls back to defaultBotId ONLY if the default was not itself evaluated —
// a default that is in the candidate set but failed its own page/audience rules has
// explicitly excluded the current page, so it must not be force-shown as the fallback.
export const resolveBot = (
  candidates: CandidateBot[],
  defaultBotId: string | null,
  ctx: VisitorContext
): string | null => {
  const matchUrl = normalizeRoutingUrl(ctx.url);
  if (ctx.debug) {
    console.log('[Getdemo] visitor context', {
      url: ctx.url,
      matchUrl: matchUrl !== ctx.url ? matchUrl : undefined,
      customParams: ctx.customParams,
      language: ctx.language
    });
  }
  const eligible = candidates.filter(bot => {
    const audience = audienceMatches(bot, ctx);
    const page = pageMatches(bot, matchUrl);
    if (ctx.debug) {
      console.log('[Getdemo] candidate', bot.id, {
        audience,
        page,
        priority: bot.widgetPriority ?? 0
      });
    }
    return audience && page;
  });

  // A "scoped" bot has page or custom-audience rules — more specific than a catch-all.
  const isScoped = (b: CandidateBot): boolean =>
    Boolean(
      (b.pageTargeting && b.pageTargeting.rules?.length) ||
        (b.audienceSegmentation &&
          b.audienceSegmentation.audienceType === 'custom' &&
          b.audienceSegmentation.segments?.length)
    );

  // Tie-break order: priority desc → more specific first → the default wins among
  // equally-generic bots → stable by id. This makes the default the fallback that shows
  // unless a higher-priority or more-specific agent matches.
  eligible.sort((a, b) => {
    const pa = a.widgetPriority ?? 0;
    const pb = b.widgetPriority ?? 0;
    if (pb !== pa) return pb - pa;
    const sa = isScoped(a);
    const sb = isScoped(b);
    if (sa !== sb) return sa ? -1 : 1;
    if (a.id === defaultBotId && b.id !== defaultBotId) return -1;
    if (b.id === defaultBotId && a.id !== defaultBotId) return 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const defaultWasEvaluated = candidates.some(c => c.id === defaultBotId);
  const fallbackBotId = defaultWasEvaluated ? null : defaultBotId;
  const chosen = eligible[0]?.id ?? fallbackBotId ?? null;
  if (ctx.debug) {
    console.log(
      '[Getdemo] resolved agent:',
      chosen,
      eligible.length ? '(rule match)' : '(default)'
    );
  }
  return chosen;
};

export default resolveBot;
